import { booksEditPermissionsFlags, booksEditPermissionsFlagsEnum, featureModuleTypesEnum } from "helpers/enums";
import { isOnlyInRole } from "helpers/roles";
import { flagVal } from "helpers/system";
import pundit from "./pundit";

const bookAccessMapper = {
    drivers: booksEditPermissionsFlagsEnum.HasAccessToEditDrivers,
    trucks: booksEditPermissionsFlagsEnum.HasAccessToEditCars,
    trailers: booksEditPermissionsFlagsEnum.HasAccessToEditTrailers,
    cargoowners: booksEditPermissionsFlagsEnum.HasAccessToEditCounterparties,
    "legal-persons": booksEditPermissionsFlagsEnum.HasAccessToEditLegalPersons,
};

const extendedViewerCreatableBooks = [
    "drivers",
    "trucks",
    "trailers",
    "transportcolumns",
    "hired-cars",
    "hired-drivers",
    "hired-trailers",
];

const UserPolicy = pundit({
    isRoot: ({
        user: {
            roles: { isRoot },
        },
    }) => isRoot,
    isAdmin: ({
        user: {
            roles: { isRoot, isAdmin, isIntegrationManager },
        },
    }) => isRoot || isAdmin || isIntegrationManager,
    isAdminWithWriteAccess: ({
        user: {
            roles: { isRoot, isTechSupport, isIntegrationManager },
        },
    }) => isRoot || isTechSupport || isIntegrationManager,
    isAdminWithLoginByUserAccess: ({
        user: {
            roles: { isRoot, isTechSupport, isIntegrationManager, isAccountManager },
        },
    }) => isRoot || isTechSupport || isIntegrationManager || isAccountManager,
    isForwarder: ({
        user: {
            roles: { isForwarder },
        },
    }) => isForwarder,
    isNotGuest: ({ user: { roles } }) => !isOnlyInRole(roles, "isGuest"),
    isCargoOwner: ({
        user: {
            roles: { isCargoOwnerAdmin, isCargoOwnerManager },
        },
    }) => isCargoOwnerAdmin || isCargoOwnerManager,
    isTruckingOrganizationAdmin: ({
        user: {
            roles: { isTruckingOrganizationAdmin },
        },
    }) => isTruckingOrganizationAdmin,
    isOnlyViewer: ({ user: { roles } }) => isOnlyInRole(roles, "isTruckingOrganizationViewer"),
    isOnlyExtendedViewer: ({ user: { roles } }) =>
        isOnlyInRole(roles, "isTruckingOrganizationExtendedViewer"),
    isReadOnlyTruckingOrganizationUser: ({ context }) =>
        context.isOnlyViewer() || context.isOnlyExtendedViewer(),
    isOnlyDispatcher: ({ user: { roles } }) => isOnlyInRole(roles, "isTruckingOrganizationDispatcher"),
    isTruckingOrg: ({
        user: {
            roles: {
                isTruckingOrganizationAdmin,
                isTruckingOrganizationManager,
                isTruckingOrganizationOperator,
                isTruckingOrganizationDispatcher,
            },
        },
    }) =>
        isTruckingOrganizationAdmin ||
        isTruckingOrganizationManager ||
        isTruckingOrganizationOperator ||
        isTruckingOrganizationDispatcher,
    hasPlanningModule: ({ user: { profile } }) =>
        !!profile &&
        profile.availableFeatureModules &&
        profile.availableFeatureModules.includes(
            featureModuleTypesEnum.PlanningCarSchedule as keyof typeof featureModuleTypesEnum,
        ),
    hasPlanEmptyMileageBidModule: ({ user: { profile } }) =>
        !!profile &&
        profile.availableFeatureModules &&
        profile.availableFeatureModules.includes(
            featureModuleTypesEnum.PlanEmptyMileageBid as keyof typeof featureModuleTypesEnum,
        ),
    canEditBook: ({
        user: { canEditAll, intBooksEditPermissionsFlags, isDemoPeriodExpired },
        context,
        options: { book },
    }) => {
        if (!canEditAll || context.isReadOnlyTruckingOrganizationUser() || context.isOnlyDispatcher() || isDemoPeriodExpired) {
            return false;
        }
        const accessName = bookAccessMapper[
            book! as keyof typeof bookAccessMapper
        ] as keyof typeof booksEditPermissionsFlagsEnum;
        const bookFlagVal = flagVal(booksEditPermissionsFlags, booksEditPermissionsFlagsEnum[accessName]);
        return (intBooksEditPermissionsFlags! & bookFlagVal) === bookFlagVal;
    },
    canCreateBook: ({ context, options: { book } }) =>
        context.canEditBook() ||
        (context.isOnlyExtendedViewer() && extendedViewerCreatableBooks.includes(book!)),
    hasWorkRestInfoAccess: ({ user: { profile }, context }) =>
        context.isTruckingOrg() &&
        !!profile &&
        profile.availableFeatureModules &&
        profile.availableFeatureModules.includes(
            featureModuleTypesEnum.PlanningCarSchedule as keyof typeof featureModuleTypesEnum,
        ),
    isFuelLimitEnabled: ({
        user: {
            profile,
            roles: {
                isRoot,
                isAdmin,
                isTruckingOrganizationAdmin,
                isTruckingOrganizationManager,
                isTruckingOrganizationHeadLogist,
                isIntegrationManager,
            },
        },
        options: { refuelingSettings },
    }) =>
        (isRoot ||
            isAdmin ||
            isTruckingOrganizationAdmin ||
            isTruckingOrganizationManager ||
            isTruckingOrganizationHeadLogist ||
            isIntegrationManager) &&
        !!profile &&
        profile.availableFeatureModules &&
        profile.availableFeatureModules.includes(
            featureModuleTypesEnum.RefuelingPlanning as keyof typeof featureModuleTypesEnum,
        ) &&
        profile.isFuelLimitEnabled &&
        !refuelingSettings?.hideRefuelingPlanning,
});

export default UserPolicy;
